"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Eye } from "lucide-react";
import { Roboto_Serif } from "next/font/google";
import { stripMarkdown } from "@/lib/utils";

const serif = Roboto_Serif({
  weight: ["400", "500", "600", "700"],
  subsets: ["cyrillic", "latin"],
});

type ArticleAuthor = {
  name: string;
  avatar?: string;
};

type ArticleStats = {
  likes: number;
  views: number;
};

type Article = {
  id: string;
  thumbnail: string;
  date: string;
  category: string;
  title: string;
  summary: string;
  author: ArticleAuthor;
  stats?: ArticleStats;
  link?: string;
  tags?: string[];
};

type ArticlePostSectionProps = {
  articles: Article[];
  categories?: string[];
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
};

const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInHours < 1) {
    return "Just now";
  } else if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }
};

const estimateReadTime = (content: string): number => {
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
};

export function ArticlePostSection({
  articles,
  categories = [],
  onLoadMore,
  hasMore = true,
  isLoading = false,
}: ArticlePostSectionProps) {
  const [activeCategory, setActiveCategory] = useState("All");

  // Filter articles by category
  const filteredArticles =
    activeCategory === "All"
      ? articles
      : activeCategory === "Newest"
      ? [...articles].sort(
          (a, b) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        )
      : articles.filter((article) => article.category === activeCategory);

  // Get hero article (first one) and grid articles (rest)
  const heroArticle = filteredArticles[0];
  const gridArticles = filteredArticles.slice(1, 5); // Show 4 in grid

  return (
    <section className="w-full bg-white py-12 px-6 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        {heroArticle && (
          <div className="mb-16">
            <Link
              href={heroArticle.link || "#"}
              className="group flex flex-col gap-6 no-underline md:flex-row md:gap-8"
            >
              {/* Hero Image */}
              <div className="flex-1 relative aspect-[16/9] md:aspect-[4/3] rounded-2xl overflow-hidden shadow-sm group-hover:shadow-md transition-all duration-300">
                <Image
                  src={heroArticle.thumbnail}
                  alt={heroArticle.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 60vw"
                />
              </div>

              {/* Hero Content */}
              <div className="flex-1 flex flex-col justify-center">
                {/* Meta Header */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    {heroArticle.author.avatar ? (
                      <Image
                        src={heroArticle.author.avatar}
                        alt={heroArticle.author.name}
                        width={24}
                        height={24}
                        className="object-cover"
                      />
                    ) : (
                      <span className="text-[10px] font-medium text-gray-600">
                        {heroArticle.author.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-medium text-gray-500 font-sans">
                    {heroArticle.author.name}
                  </span>
                  <span className="text-xs text-gray-400">•</span>
                  <time className="text-xs text-gray-500 font-sans">
                    {getRelativeTime(heroArticle.date)}
                  </time>
                </div>

                {/* Title */}
                <h2
                  className={`${serif.className} text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight`}
                >
                  {heroArticle.title}
                </h2>

                {/* Summary */}
                {heroArticle.summary && (
                  <p className="text-sm text-gray-500 font-sans leading-relaxed mb-6 line-clamp-3">
                    {stripMarkdown(heroArticle.summary)}
                  </p>
                )}

                {/* Footer */}
                <div className="flex items-center gap-3 text-xs font-sans">
                  <span className="text-red-600 font-medium hover:underline cursor-pointer">
                    {heroArticle.category}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-500">
                    {estimateReadTime(heroArticle.summary || heroArticle.title)} min read
                  </span>
                  {heroArticle.stats && heroArticle.stats.views > 0 && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="flex items-center gap-1 text-gray-500">
                        <Eye className="w-3 h-3" />
                        {heroArticle.stats.views.toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Latest News Section */}
        <div className="mb-8">
          {/* Section Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900 font-sans">
              Latest News
            </h3>
            <Link
              href="/news"
              className="flex items-center gap-1 text-red-600 font-medium text-sm hover:underline font-sans"
            >
              See all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Filter Tabs */}
          {categories.length > 0 && (
            <nav className="mb-8 overflow-x-auto no-scrollbar">
              <div className="flex gap-2 min-w-max pb-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`
                      px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap font-sans
                      ${
                        activeCategory === category
                          ? "bg-red-600 text-white shadow-md"
                          : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                      }
                    `}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </nav>
          )}

          {/* News Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {gridArticles.map((article) => {
              const CardContent = (
                <div className="flex flex-col h-full group cursor-pointer">
                  {/* Image */}
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden shadow-sm group-hover:shadow-md transition-all duration-300 mb-4">
                    <Image
                      src={article.thumbnail}
                      alt={article.title}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                    />
                  </div>

                  {/* Meta Row */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {article.author.avatar ? (
                        <Image
                          src={article.author.avatar}
                          alt={article.author.name}
                          width={16}
                          height={16}
                          className="object-cover"
                        />
                      ) : (
                        <span className="text-[8px] font-medium text-gray-600">
                          {article.author.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 font-sans font-medium truncate">
                      {article.author.name}
                    </span>
                    <span className="text-xs text-gray-400">•</span>
                    <time className="text-xs text-gray-500 font-sans whitespace-nowrap">
                      {getRelativeTime(article.date)}
                    </time>
                  </div>

                  {/* Title */}
                  <h4
                    className={`${serif.className} text-lg font-bold text-gray-900 mb-3 line-clamp-2 leading-tight`}
                  >
                    {article.title}
                  </h4>

                  {/* Excerpt */}
                  {article.summary && (
                    <p className="text-sm text-gray-500 font-sans line-clamp-3 mb-4 leading-relaxed flex-grow">
                      {stripMarkdown(article.summary)}
                    </p>
                  )}

                  {/* Footer */}
                  <div className="flex items-center gap-2 text-xs font-sans mt-auto">
                    <span className="text-red-600 font-medium hover:underline cursor-pointer">
                      {article.category}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-500">
                      {estimateReadTime(article.summary || article.title)} min read
                    </span>
                    {article.stats && article.stats.views > 0 && (
                      <>
                        <span className="text-gray-400">•</span>
                        <span className="flex items-center gap-1 text-gray-500">
                          <Eye className="w-3 h-3" />
                          {article.stats.views.toLocaleString()}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              );

              return article.link ? (
                <Link
                  key={article.id}
                  href={article.link}
                  rel="noopener noreferrer"
                  className="block h-full no-underline"
                >
                  {CardContent}
                </Link>
              ) : (
                <div key={article.id} className="h-full">
                  {CardContent}
                </div>
              );
            })}
          </div>

          {/* Show remaining articles if any */}
          {filteredArticles.length > 5 && (
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.slice(5).map((article) => {
                const CardContent = (
                  <div className="flex flex-col h-full group cursor-pointer">
                    {/* Image */}
                    <div className="relative aspect-[4/3] rounded-xl overflow-hidden shadow-sm group-hover:shadow-md transition-all duration-300 mb-4">
                      <Image
                        src={article.thumbnail}
                        alt={article.title}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      />
                    </div>

                    {/* Meta Row */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {article.author.avatar ? (
                          <Image
                            src={article.author.avatar}
                            alt={article.author.name}
                            width={16}
                            height={16}
                            className="object-cover"
                          />
                        ) : (
                          <span className="text-[8px] font-medium text-gray-600">
                            {article.author.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 font-sans font-medium truncate">
                        {article.author.name}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <time className="text-xs text-gray-500 font-sans whitespace-nowrap">
                        {getRelativeTime(article.date)}
                      </time>
                    </div>

                    {/* Title */}
                    <h4
                      className={`${serif.className} text-lg font-bold text-gray-900 mb-3 line-clamp-2 leading-tight`}
                    >
                      {article.title}
                    </h4>

                    {/* Excerpt */}
                    {article.summary && (
                      <p className="text-sm text-gray-500 font-sans line-clamp-3 mb-4 leading-relaxed flex-grow">
                        {stripMarkdown(article.summary)}
                      </p>
                    )}

                    {/* Footer */}
                    <div className="flex items-center gap-2 text-xs font-sans mt-auto">
                      <span className="text-red-600 font-medium hover:underline cursor-pointer">
                        {article.category}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-500">
                        {estimateReadTime(article.summary || article.title)} min read
                      </span>
                      {article.stats && article.stats.views > 0 && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="flex items-center gap-1 text-gray-500">
                            <Eye className="w-3 h-3" />
                            {article.stats.views.toLocaleString()}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );

                return article.link ? (
                  <Link
                    key={article.id}
                    href={article.link}
                    rel="noopener noreferrer"
                    className="block h-full no-underline"
                  >
                    {CardContent}
                  </Link>
                ) : (
                  <div key={article.id} className="h-full">
                    {CardContent}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Load More Button */}
        {hasMore && onLoadMore && (
          <div className="flex justify-center mt-8">
            <button
              onClick={onLoadMore}
              disabled={isLoading}
              className="px-8 py-3 bg-red-600 text-white rounded-full font-medium hover:bg-red-700 transition-colors disabled:opacity-50 font-sans"
            >
              {isLoading ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
